// @flow

import React, { Fragment } from 'react';

import { GET_HELP_CTA } from '@zapier/product-get-help';

type Options = {
  numberOfApps: number,
};

export default ({ numberOfApps }: Options) =>
  [
    {
      question: 'What are Tasks and how many do I need?',
      answer: (
        <Fragment>
          <p>
            A Task is counted every time a Zap successfully moves data or takes
            action for you. For example, if your Zap has an action to create new
            Google Contacts, each contact that is created will use one Task.
          </p>
          <p>
            The number of Tasks you need depends on the number of active Zaps
            you have and how frequently those Zaps will run to automate your
            workflow. Don’t worry, if you use up all your Tasks in a given month
            we’ll hold your Tasks until you upgrade or your monthly usage
            resets. If you use more than 2 million Tasks, you will be billed at
            a per-task rate of $0.0010.
          </p>
        </Fragment>
      ),
    },
    {
      question: 'What are Apps?',
      answer: `An App is a web service or application, such as Google Docs, Evernote or Salesforce. Zapier offers integrations for more than ${numberOfApps} apps, letting you easily move data between them to automate repetitive tasks.`,
    },
    {
      question: 'Do you have a free trial of your premium features?',
      answer: (
        <p>
          Yes, when you create a new Zapier account, you’re instantly enrolled
          in a{' '}
          <a
            href="/help/account/billing-pricing/understand-your-free-zapier-trial"
            target="_blank"
          >
            free 14-day trial
          </a>{' '}
          of the Zapier Professional plan.
        </p>
      ),
    },
    {
      question: 'Can I change my plan later?',
      answer:
        "Yes, you can upgrade or downgrade at any time. If you choose to upgrade, you'll pay a pro-rated amount for the rest of the month. If you choose to downgrade, you'll be credited on next month's bill.",
    },
    {
      question: 'What if I decide to cancel?',
      answer: (
        <p>
          If you no longer wish to use Zapier, you may{' '}
          <a
            href="/help/account/billing-pricing/change-or-cancel-your-zapier-plan"
            target="_blank"
          >
            cancel
          </a>{' '}
          at any time. You will receive a pro-rated credit with Zapier for the
          remainder of that month's billing cycle. See our full refund policy{' '}
          <a href="/help/refund-policy/" target="_blank">
            here
          </a>
          .
        </p>
      ),
    },
    {
      question: 'Can you charge me in my local currency?',
      answer:
        'No, at the moment we only accept payment in United States Dollars (USD). We do accept all major credit/debit cards as well as PayPal.',
    },
    {
      question: 'Do you offer any discounts?',
      answer: (
        <p>
          Yes! When you choose to pre-pay for a year of service, we’ll give you
          a 20% discount. We also offer an additional 15% discount for
          non-profits. Learn more at{' '}
          <a href="/non-profits" target="_blank">
            https://zapier.com/non-profits/
          </a>
          .
        </p>
      ),
    },
    {
      question: 'Do I need to know how to code?',
      answer:
        "Nope! Zapier makes it easy for you to move data between your apps without being a developer. We may need you to locate information for certain accounts, but we'll guide you through any additional steps required and our support champions are here to help you by email should you need assistance.",
    },
    {
      question: 'What if I have more questions?',
      answer: (
        <p>
          Try searching for what you need in our{' '}
          <a href="/support" target="_blank">
            Help &amp; Support
          </a>{' '}
          site. If you don't find what you're looking for, you can{' '}
          <a href="/app/contact-us?topic=account" target="_blank">
            {GET_HELP_CTA}
          </a>{' '}
          here.
        </p>
      ),
    },
  ].filter(Boolean);
